import React, { useContext, useCallback, useRef } from 'react'
import { useIntl } from 'react-intl'
import { App } from 'antd'
import { ClientContext, useSubscription } from 'graphql-hooks'
import cloneDeep from 'lodash/cloneDeep'

import { notificationStore } from '../../context/notificationsContext/store'
import { errorSubscription } from '../../graphql/custom-queries'
import { generateNotificationInfo, recordUntrackedOutcome } from '../../util/notifications'
import { markCrispTrialTriggerReceived, runCrispTrigger } from '../../util/set-crisp-session-info'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectUserIdSlice } from '../../store/user-id-slice'

import { ISubscriptionEvent, ISubscriptionEventMessage } from '@vacationtracker/shared/types/notification'
import { setFetchOnboarding } from '../../store/onboarding-slice'
import logger from '@vacationtracker/shared/functions/logger'
import { updateGraphQlAuthHeaderAndSubscription } from '../../util/update-graphql-header-and-subscription'
import { apiCache } from '../../util/cache-api-response'

type EventResponse = {
  eventResponses: ISubscriptionEvent
}

const Notifications = () => {
  const { actionNotifications, setActionNotifications, eventsNotifications, setEventsNotifications } = useContext(notificationStore)
  // A bulk action's events arrive in bursts - a move posts them eight at a time - so several land
  // before React has re-rendered with the result of the first. Reading the state the handler closed
  // over meant each of them wrote back a list only its own event had been taken out of, the last
  // write won, and the group never emptied: its progress notification sat there at a part count
  // forever. The ref carries the drained list from one event to the next within the same tick.
  const actionNotificationsRef = useRef(actionNotifications)
  const eventsNotificationsRef = useRef(eventsNotifications)
  actionNotificationsRef.current = actionNotifications
  eventsNotificationsRef.current = eventsNotifications
  const { userId } = useAppSelector(selectUserIdSlice)
  const gqlClient = useContext(ClientContext)
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()
  const { notification } = App.useApp()

  const handleSubscriptionEvent = useCallback(
    ({ data, errors }: { data?: EventResponse; errors?: object[] }) => {
      // The "errors" type is `object[] | undefined`, but WebSocket error returns just one object with `type: 'error'`
      const err: any = errors
      if (errors && (err instanceof Error || !Array.isArray(err))) {
        if (gqlClient) {
          // Renew the token
          updateGraphQlAuthHeaderAndSubscription({
            force: true,
            graphQlClient: gqlClient,
          })
        }
        return
      } else if (errors || !data) {
        logger.error(...(errors || []))
        return
      }

      let subscriptionEvent: ISubscriptionEventMessage | null = null
      try {
        subscriptionEvent = JSON.parse(data.eventResponses.originalEvent)
      } catch (err) {
        logger.error('Error parsing subscription event (Notifications)', err)
      }

      if (subscriptionEvent?.eventType === 'CUSTOMERIO_CAMPAIGN_EVENT_RECEIVED') {
        runCrispTrigger(subscriptionEvent.campaignId as string).then(() => {
          markCrispTrialTriggerReceived(subscriptionEvent.campaignId as string)
        })
      }

      // Clear cache to ensure fresh data is fetched
      apiCache.clear()

      // Ignore if event not created from dashboard
      if (subscriptionEvent?.source !== 'dashboard') {
        return
      }

      const eventType = subscriptionEvent.eventType
      if (eventType === 'LEAVE_REQUEST_CREATED' || eventType === 'NOTIFICATION_CREATED' || eventType === 'USER_STATUS_CHANGED') {
        dispatch(setFetchOnboarding(subscriptionEvent.correlationId))
      }

      let eventHasPartialError = false
      try {
        eventHasPartialError = eventType === 'IMPORT_USERS' && JSON.parse(data.eventResponses.result || '{}')?.skipped?.length > 0
      } catch (err) {
        /* Generic message */
      }

      const currentActionNotifications = actionNotificationsRef.current
      const currentEventsNotifications = eventsNotificationsRef.current

      if (
        data?.eventResponses?.code === 'error' ||
        (subscriptionEvent && Array.isArray(currentActionNotifications) && subscriptionEvent.correlationId && currentActionNotifications.includes(subscriptionEvent.correlationId))
      ) {
        const drained = cloneDeep(currentActionNotifications)
        const notifications = updateNotification(data, drained)
        // The object path is drained in place; the array path is filtered into a new list, so an
        // early return out of updateNotification keeps this id - a sibling event under the same
        // correlation id still needs it to match.
        actionNotificationsRef.current = (notifications ?? drained) as string[]
        setActionNotifications(actionNotificationsRef.current)
      } else {
        recordUntrackedOutcome(subscriptionEvent.correlationId, eventType, 'success')
      }

      if (
        data?.eventResponses?.code === 'error' ||
        eventHasPartialError ||
        (subscriptionEvent &&
          subscriptionEvent.correlationId &&
          currentEventsNotifications &&
          currentEventsNotifications[subscriptionEvent.correlationId] &&
          currentEventsNotifications[subscriptionEvent.correlationId].eventType === subscriptionEvent.eventType) ||
        currentEventsNotifications[`${subscriptionEvent.correlationId}#${subscriptionEvent.eventType}`]
      ) {
        const drained = cloneDeep(currentEventsNotifications)
        const notifications = updateNotification(data, drained)
        eventsNotificationsRef.current = (notifications ?? drained) as object
        setEventsNotifications(eventsNotificationsRef.current)
      }
    },
    [setActionNotifications, setEventsNotifications, gqlClient, dispatch, formatMessage, notification]
  )

  useSubscription<EventResponse>(
    {
      query: errorSubscription,
      variables: { userId },
    },
    handleSubscriptionEvent
  )

  const getOverriddenDescription = (message: string, data: any, formatMessage: any): string | null => {
    if (message === 'Payment failed' && data?.eventResponses?.data) {
      try {
        const errorData = JSON.parse(data.eventResponses.data)
        if (errorData.message && errorData.message.startsWith('This customer has no attached payment source or default payment method')) {
          return formatMessage({ id: 'billing.paymentSourceMissing' })
        }
      } catch (err) {
        // If parsing fails, return null
      }
    }
    return null
  }

  const updateNotification = (data, eventNotificationsRef): { key: string } | void | object => {
    const {
      action,
      notifications,
      options: { title, titleValues, message, description, descriptionValues, btn, duration, key, updateNotification, forceErrorMessage = false },
    } = generateNotificationInfo(data.eventResponses, eventNotificationsRef, formatMessage)
    if (!updateNotification || (!title && action !== 'error')) {
      return
    }

    const overriddenDescription = getOverriddenDescription(message, data, formatMessage)

    const messageValue =
      title && formatMessage({ id: title }, titleValues)
        ? formatMessage({ id: title }, titleValues)
        : /^error\.[a-zA-Z0-9]+$/.test(message)
          ? formatMessage({ id: message })
          : message

    const errorMessage = action === 'error' ? formatMessage({ id: 'notifications.error' }, { correlationId: key }) : ''

    let descriptionValue

    // Use overridden description if available (for billing payment source missing)
    if (overriddenDescription) {
      descriptionValue = (
        <>
          {overriddenDescription} {errorMessage}
        </>
      )
    } else if (typeof description !== 'object') {
      descriptionValue = description && `${formatMessage({ id: description }, descriptionValues)} ${errorMessage}`
    } else {
      descriptionValue = (
        <>
          {description as React.ReactNode} {errorMessage}
        </>
      )
    }
    if (forceErrorMessage) {
      descriptionValue = <>{errorMessage}</>
    }

    if (action === 'close') {
      notification.destroy(key)
      return notifications
    }

    notification[action]({
      key,
      message: messageValue,
      description: descriptionValue,
      btn,
      duration,
    })

    return notifications
  }

  return <></>
}

export default Notifications
